import React, { useState, useEffect, useCallback } from 'react';
import {
  connectGoogle,
  disconnectGoogle,
  isConsented,
  listCalendars,
} from './googleCalendar';
import { HISTORY_YEARS, IMPORT_DAYS_AHEAD, EXPORT_DAYS_BACK } from './googleConfig';
import { SHORTCUTS } from './keyboard';
import { getConfig } from './appConfig';
import SetupScreen from './SetupScreen';

const SETTINGS_KEY = 'mytasklog_gcal_settings';

/** 端末内の控え。ログイン前や通信できないときに使う */
export const loadCalendarSettings = () => {
  let s = {};
  try {
    s = JSON.parse(localStorage.getItem(SETTINGS_KEY)) || {};
  } catch {
    s = {};
  }
  if (!Array.isArray(s.inputCalendarIds)) {
    s.inputCalendarIds = s.inputCalendarId ? [s.inputCalendarId] : [];
  }
  delete s.inputCalendarId;
  return s;
};

export const saveCalendarSettingsLocal = (s) =>
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));

/** 利用をやめるときの手順 */
function TeardownGuide({ onBack }) {
  const config = getConfig() || {};
  const projectId = config.projectId || '（あなたのプロジェクトID）';

  return (
    <div className="teardown">
      <p className="setup-lead">
        このアプリの記録は、あなた自身のFirebaseプロジェクトに保存されています。
        利用をやめる場合は、以下の手順でプロジェクトごと削除してください。
        削除するとタスクの記録もすべて消えます。
      </p>

      <div className="teardown-target">
        <span>削除するプロジェクト</span>
        <b>{projectId}</b>
      </div>

      <section className="setup-section note">
        <h3>手順</h3>
        <ol className="setup-steps">
          <li>
            <b>Firebase コンソール</b>（console.firebase.google.com）を開く
          </li>
          <li>上記のプロジェクトを選び、左上の歯車 →「プロジェクトの設定」</li>
          <li>「全般」タブを一番下までスクロールし、「このプロジェクトを削除」</li>
          <li>確認画面でプロジェクトIDを入力して削除を実行</li>
          <li>
            <b>Google Cloud Console</b>（console.cloud.google.com）でも
            同じプロジェクトが残っている場合は、「IAMと管理」→「設定」→
            「シャットダウン」で削除
          </li>
        </ol>

        <p className="gcal-desc small">
          FirebaseとGoogle Cloudは同じプロジェクトを共有しているため、
          どちらか一方で削除すれば両方に反映されます。
          反映まで30日ほどの猶予期間があり、その間なら復元できます。
        </p>
      </section>

      <section className="setup-section note">
        <h3>そのほか</h3>
        <p className="gcal-desc small">
          このブラウザに残っている設定は、プロジェクトを削除すれば接続先が
          なくなるため、そのままにして構いません。完全に消す場合は、
          ブラウザの設定からサイトデータを削除してください。
        </p>
        <p className="gcal-desc small">
          Googleカレンダーに書き出した実績は、カレンダー側に残ります。
          不要であればカレンダーから削除してください。
        </p>
      </section>

      <div className="setup-actions">
        <button className="setup-cancel" onClick={onBack}>
          戻る
        </button>
      </div>
    </div>
  );
}

function SettingsModal({
  settings,
  onSettingsChange,
  syncState,
  onClose,
  user,
  onSignIn,
  onSignOut,
  saveState,
  onDownloadHistory,
  historyState,
  tokenLive,
}) {
  const [connected, setConnected] = useState(isConsented());
  const [calendars, setCalendars] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [subScreen, setSubScreen] = useState(null); // 'setup' | 'teardown' | null

  const fetchCalendars = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listCalendars();
      setCalendars(list);
      setConnected(true);
    } catch (e) {
      setError(e.message);
      setConnected(isConsented());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isConsented()) fetchCalendars();
  }, [fetchCalendars, tokenLive]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !subScreen && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, subScreen]);

  const handleSignIn = async () => {
    setAuthError('');
    try {
      await onSignIn();
    } catch (e) {
      setAuthError(e.message);
    }
  };

  const handleConnect = async () => {
    setError('');
    try {
      await connectGoogle();
      await fetchCalendars();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDisconnect = () => {
    disconnectGoogle();
    setConnected(false);
    setCalendars([]);
  };

  const inputIds = settings.inputCalendarIds || [];

  const toggleInput = (id) => {
    const next = inputIds.includes(id)
      ? inputIds.filter((x) => x !== id)
      : [...inputIds, id];
    onSettingsChange({ ...settings, inputCalendarIds: next });
  };

  // ===== 別画面（接続設定 / 利用終了）=====
  if (subScreen) {
    const title = subScreen === 'setup' ? '接続設定の変更' : '利用を終了する';
    return (
      <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
        <div className="modal wide" onClick={(e) => e.stopPropagation()}>
          <div className="modal-head">
            <h2>{title}</h2>
            <button className="modal-close" onClick={() => setSubScreen(null)} title="戻る">
              ×
            </button>
          </div>
          <div className="modal-body">
            {subScreen === 'setup' ? (
              <SetupScreen onCancel={() => setSubScreen(null)} />
            ) : (
              <TeardownGuide onBack={() => setSubScreen(null)} />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>設定</h2>
          <button className="modal-close" onClick={onClose} title="閉じる">
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* ===== アカウント ===== */}
          <section className="settings-section">
            <div className="gcal-head">
              <h3>アカウント</h3>
              {user ? (
                <div className="gcal-head-actions">
                  <span className="gcal-status ok">ログイン中</span>
                  <button className="gcal-btn ghost" onClick={onSignOut}>
                    ログアウト
                  </button>
                </div>
              ) : (
                <button className="gcal-btn" onClick={handleSignIn}>
                  Googleでログイン
                </button>
              )}
            </div>

            {authError && <div className="gcal-error">{authError}</div>}

            {user ? (
              <>
                <div className="account-row">
                  {user.photoURL && (
                    <img className="account-avatar" src={user.photoURL} alt="" />
                  )}
                  <div className="account-meta">
                    <div className="account-name">{user.displayName}</div>
                    <div className="account-mail">{user.email}</div>
                  </div>
                </div>

                <div className="gcal-foot" style={{ marginTop: 12 }}>
                  <button
                    className="gcal-btn small"
                    onClick={onDownloadHistory}
                    disabled={historyState.busy}
                  >
                    {historyState.busy
                      ? '取得中...'
                      : `過去${HISTORY_YEARS}年分の記録を取得`}
                  </button>
                  {historyState.done && <span className="gcal-note">取得しました</span>}
                </div>
                {historyState.error && <div className="gcal-error">{historyState.error}</div>}

                <p className="gcal-desc small">
                  いま開いている日の記録は、他の端末とリアルタイムで同期されます。
                  過去・当日・未来のどの日でも同じです。
                  端末を変えたばかりで手元に記録がないときは、
                  上のボタンで過去の記録をまとめて取り込めます。
                  {saveState === 'error' && '（現在オフラインか、保存に失敗しています）'}
                </p>
              </>
            ) : (
              <p className="gcal-desc">
                ログインすると、記録がクラウドに保存され、複数の端末で同じ記録を使えます。
              </p>
            )}
          </section>

          <hr className="settings-divider" />

          {/* ===== Googleカレンダー連携 ===== */}
          <section className="settings-section">
            <div className="gcal-head">
              <h3>Googleカレンダー連携</h3>
              {connected ? (
                <div className="gcal-head-actions">
                  <span className="gcal-status ok">接続中</span>
                  <button className="gcal-btn ghost" onClick={handleDisconnect}>
                    解除
                  </button>
                </div>
              ) : (
                <button className="gcal-btn" onClick={handleConnect}>
                  Googleと接続
                </button>
              )}
            </div>

            {error && (
              <div className="gcal-error">
                {error}
                <div style={{ marginTop: 8 }}>
                  <button className="gcal-btn small" onClick={handleConnect}>
                    接続を更新
                  </button>
                </div>
              </div>
            )}

            {!connected && !error && (
              <p className="gcal-desc">
                接続すると、カレンダーで組んだ予定を取り込んだり、
                完了した実績をカレンダーに書き出したりできます。
              </p>
            )}

            {connected && (
              <>
                {loading ? (
                  <div className="gcal-note">カレンダーを読み込み中...</div>
                ) : (
                  <>
                    <div className="gcal-field">
                      <div className="gcal-label">
                        取り込み元（明日から{IMPORT_DAYS_AHEAD}日先の予定）
                        <span className="gcal-sub">複数選べます</span>
                      </div>
                      <div className="gcal-checklist">
                        {calendars.map((c) => (
                          <label key={c.id} className="gcal-check">
                            <input
                              type="checkbox"
                              checked={inputIds.includes(c.id)}
                              onChange={() => toggleInput(c.id)}
                            />
                            <span>
                              {c.name}
                              {c.primary ? '（メイン）' : ''}
                            </span>
                          </label>
                        ))}
                        {calendars.length === 0 && (
                          <div className="gcal-note">カレンダーがありません</div>
                        )}
                      </div>
                    </div>

                    <div className="gcal-field">
                      <div className="gcal-label">書き出し先（完了した実績）</div>
                      <select
                        value={settings.outputCalendarId || ''}
                        onChange={(e) =>
                          onSettingsChange({
                            ...settings,
                            outputCalendarId: e.target.value || null,
                          })
                        }
                      >
                        <option value="">使わない</option>
                        {calendars
                          .filter((c) => c.canWrite)
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                              {c.primary ? '（メイン）' : ''}
                            </option>
                          ))}
                      </select>
                    </div>
                  </>
                )}

                {syncState.lastSyncAt && (
                  <div className="gcal-foot">
                    <span className="gcal-note">最終同期 {syncState.lastSyncAt}</span>
                  </div>
                )}

                {syncState.error && <div className="gcal-error">{syncState.error}</div>}

                <p className="gcal-desc small">
                  カレンダーとのやりとりは、画面上部の ⟳ を押したときだけ行われます。
                  予定の取り込みは明日から{IMPORT_DAYS_AHEAD}日先まで。実績の書き出しは、
                  当日と過去{EXPORT_DAYS_BACK}日ぶんのうち、まだ送っていないものが対象です。
                  {user && 'この選択は他の端末とも共有されます。'}
                </p>
              </>
            )}
          </section>

          <hr className="settings-divider" />

          {/* ===== キーボード操作 ===== */}
          <section className="settings-section">
            <div className="gcal-head">
              <h3>キーボード操作</h3>
            </div>
            <ul className="shortcut-list">
              {SHORTCUTS.map((s) => (
                <li key={s.text}>
                  <span className="shortcut-keys">
                    {s.keys.map((k, i) => (
                      <React.Fragment key={k}>
                        {i > 0 && <span className="shortcut-plus">+</span>}
                        <kbd>{k}</kbd>
                      </React.Fragment>
                    ))}
                  </span>
                  <span className="shortcut-text">{s.text}</span>
                </li>
              ))}
            </ul>
            <p className="gcal-desc small">
              Enter だけでは追加されません。日本語を確定したときに、
              続けて押しても予定に入らないようにしてあります。
            </p>
          </section>

          <hr className="settings-divider" />

          {/* ===== 接続設定 ===== */}
          <section className="settings-section">
            <div className="gcal-head">
              <h3>接続設定</h3>
              <button className="gcal-btn ghost" onClick={() => setSubScreen('setup')}>
                変更
              </button>
            </div>
            <p className="gcal-desc small">
              FirebaseとGoogle Cloudの接続情報を確認・変更できます。
              保存するとページが読み込み直されます。
              この情報は端末ごとに入力が必要ですが、
              「設定をコピー」から他の端末へ引き継げます。
            </p>
          </section>

          <hr className="settings-divider" />

          {/* ===== 利用終了 ===== */}
          <section className="settings-section">
            <div className="gcal-head">
              <h3>利用を終了する</h3>
              <button className="gcal-btn ghost" onClick={() => setSubScreen('teardown')}>
                手順を見る
              </button>
            </div>
            <p className="gcal-desc small">
              記録を消して利用をやめる場合の手順を表示します。
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default SettingsModal;
